/**
 * PricingView — PadelPulse public pricing page
 *
 * A read-only, no-login B2B page for prospective organizers. It leads with the
 * platform's value metrics (automated tournament engines, insights dashboard,
 * copy-and-send WhatsApp credentials flow) and presents three transparent
 * plans. Content is config-driven: the plan structure lives in `PLANS` below,
 * while every user-facing string is resolved through i18n so the page stays
 * fully localized (en/es) without a CMS.
 */

import { t, type TranslationKey } from "../i18n/config";
import { renderPublicHeader, renderPublicFooter, setupPublicChrome } from "./publicChrome";

/**
 * Plan definition. `featured` renders the highlighted "most popular" card, and
 * `featureKeys` are i18n keys (typed, so a missing key is a compile error)
 * listing the bullet points for the plan.
 */
interface Plan {
  id: "starter" | "club" | "pro";
  featured: boolean;
  nameKey: TranslationKey;
  priceKey: TranslationKey;
  periodKey: TranslationKey;
  descKey: TranslationKey;
  featureKeys: TranslationKey[];
}

const PLANS: Plan[] = [
  {
    id: "starter",
    featured: false,
    nameKey: "pricing.plan.starter.name",
    priceKey: "pricing.plan.starter.price",
    periodKey: "pricing.plan.starter.period",
    descKey: "pricing.plan.starter.desc",
    featureKeys: [
      "pricing.plan.starter.f1",
      "pricing.plan.starter.f2",
      "pricing.plan.starter.f3",
      "pricing.plan.starter.f4",
    ],
  },
  {
    id: "club",
    featured: true,
    nameKey: "pricing.plan.club.name",
    priceKey: "pricing.plan.club.price",
    periodKey: "pricing.plan.club.period",
    descKey: "pricing.plan.club.desc",
    featureKeys: [
      "pricing.plan.club.f1",
      "pricing.plan.club.f2",
      "pricing.plan.club.f3",
      "pricing.plan.club.f4",
      "pricing.plan.club.f5",
    ],
  },
  {
    id: "pro",
    featured: false,
    nameKey: "pricing.plan.pro.name",
    priceKey: "pricing.plan.pro.price",
    periodKey: "pricing.plan.pro.period",
    descKey: "pricing.plan.pro.desc",
    featureKeys: [
      "pricing.plan.pro.f1",
      "pricing.plan.pro.f2",
      "pricing.plan.pro.f3",
      "pricing.plan.pro.f4",
    ],
  },
];

const VALUE_CARDS: { icon: string; titleKey: TranslationKey; bodyKey: TranslationKey }[] = [
  { icon: "account_tree", titleKey: "pricing.value.card1.title", bodyKey: "pricing.value.card1.body" },
  { icon: "insights", titleKey: "pricing.value.card2.title", bodyKey: "pricing.value.card2.body" },
  { icon: "forum", titleKey: "pricing.value.card3.title", bodyKey: "pricing.value.card3.body" },
];

export class PricingView {
  private container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="min-h-screen bg-background-light dark:bg-background-dark daylight:bg-day-canvas flex flex-col">
        ${renderPublicHeader("pricing")}

        <main class="flex-1">
          ${this.renderHero()}
          ${this.renderPlans()}
          ${this.renderValueSection()}
        </main>

        ${renderPublicFooter()}
      </div>
    `;
    setupPublicChrome(this.container);
  }

  private renderHero(): string {
    return `
      <section class="relative overflow-hidden py-16 lg:py-24">
        <div class="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-accent/10 dark:from-primary/5 dark:via-transparent dark:to-accent/5"></div>
        <div class="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <span class="badge badge-primary">${t("pricing.hero.badge")}</span>
          <h1 class="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 dark:text-white daylight:text-day-ink leading-tight">
            ${t("pricing.hero.titleLead")} <span class="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">${t("pricing.hero.titleAccent")}</span>
          </h1>
          <p class="text-lg md:text-xl text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft max-w-2xl mx-auto">
            ${t("pricing.hero.subtitle")}
          </p>
        </div>
      </section>
    `;
  }

  private renderValueSection(): string {
    const cards = VALUE_CARDS.map(
      (card) => `
        <div class="card space-y-4">
          <div class="inline-flex items-center justify-center size-12 rounded-xl bg-primary/10 dark:bg-primary/5 text-primary daylight:text-day-ink">
            <span class="material-symbols-outlined">${card.icon}</span>
          </div>
          <h3 class="text-xl font-bold text-slate-900 dark:text-white daylight:text-day-ink">${t(card.titleKey)}</h3>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t(card.bodyKey)}</p>
        </div>`
    ).join("");

    return `
      <section class="py-16 lg:py-24 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="max-w-3xl mb-12">
            <h2 class="text-3xl md:text-4xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${t("pricing.value.title")}</h2>
            <p class="text-lg text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-3">${t("pricing.value.subtitle")}</p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            ${cards}
          </div>
        </div>
      </section>
    `;
  }

  private renderPlans(): string {
    const cards = PLANS.map((plan) => this.renderPlanCard(plan)).join("");

    return `
      <section class="py-16 lg:py-24 border-t border-slate-200 dark:border-slate-800 daylight:border-day-ink/10 bg-gradient-to-r from-primary/5 to-accent/5 dark:from-primary/5 dark:to-accent/5">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div class="max-w-3xl mx-auto text-center mb-12">
            <h2 class="text-3xl md:text-4xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${t("pricing.plans.title")}</h2>
            <p class="text-lg text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft mt-3">${t("pricing.plans.subtitle")}</p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
            ${cards}
          </div>
        </div>
      </section>
    `;
  }

  private renderPlanCard(plan: Plan): string {
    const highlight = plan.featured
      ? "border-primary ring-2 ring-primary/40 shadow-2xl daylight:shadow-day-lg"
      : "border-slate-200 dark:border-slate-800 daylight:border-day-ink/10";

    const popularBadge = plan.featured
      ? `<span class="badge badge-primary absolute -top-3 left-1/2 -translate-x-1/2">${t("pricing.plans.badgePopular")}</span>`
      : "";

    const period = t(plan.periodKey);
    const periodMarkup = period
      ? `<span class="text-base font-medium text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">${period}</span>`
      : "";

    const features = plan.featureKeys
      .map(
        (key) => `
          <li class="flex items-start gap-3">
            <span class="material-symbols-outlined text-success flex-shrink-0" style="font-size: 20px;">check_circle</span>
            <span class="text-slate-600 dark:text-slate-300 daylight:text-day-ink-soft">${t(key)}</span>
          </li>`
      )
      .join("");

    const ctaClass = plan.featured ? "btn-primary" : "btn-outline";

    return `
      <div class="relative flex flex-col rounded-2xl border bg-white dark:bg-surface-dark daylight:bg-day-raised p-6 lg:p-8 ${highlight}">
        ${popularBadge}
        <h3 class="text-2xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${t(plan.nameKey)}</h3>
        <div class="mt-4 flex items-baseline gap-1">
          <span class="text-4xl font-black text-slate-900 dark:text-white daylight:text-day-ink">${t(plan.priceKey)}</span>
          ${periodMarkup}
        </div>
        <p class="mt-3 text-sm text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${t(plan.descKey)}</p>
        <ul class="mt-6 space-y-3 flex-1">
          ${features}
        </ul>
        <a href="#landing" class="${ctaClass} w-full justify-center mt-8">
          <span class="material-symbols-outlined">badge</span>
          <span>${t("pricing.plans.cta")}</span>
        </a>
      </div>
    `;
  }
}
